import React, { useState } from 'react';
import { Container, Form, InputGroup, Button, Alert, Spinner, Badge, Card } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listTools, pickupTool } from '../../services/toolAssignmentService';
import { useAuth } from '../../contexts/AuthContext';
import ToolRequestModal from './components/ToolRequestModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ToolList.css';

const CONDITION_LABELS = { good: 'Buena', damaged: 'Dañada', lost: 'Perdida', maintenance: 'Mantenimiento' };
const CONDITION_COLORS = { good: 'success', damaged: 'warning', lost: 'danger', maintenance: 'info' };

const WarehousePage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isTechnician, isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [taking, setTaking] = useState(null);
  const [requestTool, setRequestTool] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['warehouse-page', search],
    queryFn: () => listTools({
      search: search || undefined,
      status: 'available',
      limit: 100
    }),
    staleTime: 30 * 1000
  });

  const takeMutation = useMutation({
    mutationFn: (toolId) => pickupTool({ tool_id: toolId }),
    onSuccess: ({ message, error: err }) => {
      setTaking(null);
      if (err) { toast.error(err.message); return; }
      toast.success(message || 'Herramienta tomada de bodega.');
      queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-page'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-browse'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
    },
    onError: (e) => { setTaking(null); toast.error(e.message); }
  });

  if (!isTechnician && !isAdmin) {
    return <Alert variant="danger" className="m-3">Esta página requiere autenticación.</Alert>;
  }

  const tools = data?.data || [];

  return (
    <DashboardLayout>
    <Container fluid className="py-3 px-3" style={{ maxWidth: 720 }}>
      <div className="d-flex align-items-center mb-3">
        <Button
          variant="link"
          className="p-0 me-2 text-decoration-none"
          onClick={() => navigate('/mis-herramientas')}
          aria-label="Volver a mis herramientas"
        >
          <i className="bi bi-arrow-left fs-4" />
        </Button>
        <h4 className="mb-0 d-flex align-items-center gap-2">
          <i className="bi bi-shop" />
          Bodega
        </h4>
        <Button
          as={Link}
          to="/herramientas/mis-solicitudes"
          variant="link"
          size="sm"
          className="ms-auto text-decoration-none"
        >
          <i className="bi bi-clipboard-check me-1" />Mis solicitudes
        </Button>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
        <Form.Control
          placeholder="Buscar por nombre o serial..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          inputMode="search"
          autoComplete="off"
          style={{ fontSize: '1rem', height: 48 }}
        />
        {search && (
          <Button variant="outline-secondary" onClick={() => setSearch('')} aria-label="Limpiar búsqueda">
            <i className="bi bi-x" />
          </Button>
        )}
      </InputGroup>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && (
        <Alert variant="danger">No se pudo cargar el catálogo: {error.message}</Alert>
      )}

      {!isLoading && !error && tools.length === 0 && (
        <Alert variant="info" className="text-center">
          {search
            ? <>Sin resultados para "<strong>{search}</strong>". ¿Querías una herramienta que no está registrada? Pedile al admin que la registre.</>
            : 'Bodega vacía. Todas las herramientas están asignadas en este momento.'}
        </Alert>
      )}

      <ToolRequestModal
        show={!!requestTool}
        tool={requestTool}
        onHide={() => setRequestTool(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['warehouse-page'] });
          queryClient.invalidateQueries({ queryKey: ['my-tool-requests'] });
        }}
      />

      {!isLoading && tools.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <small className="text-muted px-1">
            {tools.length} disponible{tools.length !== 1 ? 's' : ''}
          </small>
          {tools.map((tool) => {
            const isTaking = taking === tool.id;
            const needsApproval = tool.requires_approval === true;
            return (
              <Card key={tool.id} className="modern-card" style={{ borderRadius: 12 }}>
                <Card.Body className="p-3">
                  <div className="d-flex gap-3 align-items-center">
                    {tool.image_url ? (
                      <img
                        src={tool.image_url}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 72, height: 72, objectFit: 'cover',
                          borderRadius: 10, flexShrink: 0, background: '#f8f9fa'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 72, height: 72, background: '#f8f9fa',
                        borderRadius: 10, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className="bi bi-tools text-muted fs-3" />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <strong className="text-truncate" style={{ fontSize: '1rem' }}>
                          {tool.name}
                        </strong>
                        {needsApproval && (
                          <Badge bg="warning" text="dark" title="Requiere aprobación del supervisor">
                            <i className="bi bi-shield-check me-1" />
                            Aprobación
                          </Badge>
                        )}
                        {tool.condition && tool.condition !== 'good' && (
                          <Badge bg={CONDITION_COLORS[tool.condition]}>
                            {CONDITION_LABELS[tool.condition]}
                          </Badge>
                        )}
                      </div>
                      <code className="text-muted d-block" style={{ fontSize: '0.85rem' }}>
                        {tool.serial_number}
                      </code>
                      {(tool.brand || tool._categoryLabel) && (
                        <small className="text-muted">
                          {[tool._categoryLabel, tool.brand, tool.model]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      )}
                    </div>
                  </div>
                  {needsApproval ? (
                    <Button
                      variant="warning"
                      onClick={() => setRequestTool(tool)}
                      className="w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                      style={{ minHeight: 44, fontWeight: 500 }}
                    >
                      <i className="bi bi-clipboard-check" />
                      Solicitar
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => { setTaking(tool.id); takeMutation.mutate(tool.id); }}
                      disabled={isTaking}
                      className="w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                      style={{ minHeight: 44, fontWeight: 500 }}
                    >
                      {isTaking ? (
                        <>
                          <Spinner size="sm" animation="border" />
                          Tomando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-hand-index" />
                          Tomar esta herramienta
                        </>
                      )}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </Container>
    </DashboardLayout>
  );
};

export default WarehousePage;
